import type { ReactNode } from "react";
import { cn } from "@/shared/lib/cn";
import { useScrollGlobe } from "../useScrollGlobe";
import { ScrollGlobeNav } from "./ScrollGlobeNav";
import { ScrollGlobeSectionBlock } from "./ScrollGlobeSectionBlock";
import { ScrollProgressBar } from "./ScrollProgressBar";
import type { ScrollGlobeSection } from "./scrollGlobe.types";

export interface ScrollGlobeProps {
  sections: readonly ScrollGlobeSection[];
  /** La pieza que flota entre secciones. Aquí, el vídeo circular. */
  globe: ReactNode;
  /** `false` = la persona pidió movimiento reducido. */
  motionOk: boolean;
  className?: string;
}

/**
 * La landing inmersiva: secciones de alto de pantalla y una pieza que
 * viaja entre ellas mientras se desplaza.
 *
 * ── Qué hace este archivo ────────────────────────────────────────────
 * Compone y nada más. La mecánica (qué sección manda, dónde va el globo,
 * cuánto se ha recorrido) vive en `useScrollGlobe`; la barra, la
 * navegación y cada sección son componentes con su archivo. Aquí solo se
 * conectan.
 *
 * ── El globo es inyectable ───────────────────────────────────────────
 * El componente del que sale esto traía una Tierra 3D dentro. Se pasa por
 * `globe` para que la pieza que flota —el vídeo de marca— no obligue a
 * este archivo a saber nada de vídeos, ni a cargarlos en una prueba que
 * solo quiere comprobar el texto de las secciones.
 *
 * ── Con movimiento reducido el globo NO vuela ────────────────────────
 * Se queda clavado en su primera posición, sin transición: la pieza sigue
 * ahí —es la imagen de marca de la página— pero deja de desplazarse y de
 * escalar a cada sección, que es lo que la preferencia viene a apagar.
 * También se van la transición de la barra de progreso, el suavizado al
 * saltar de sección y la etiqueta que se desvanece sola.
 *
 * ── Candidato a `shared/ui` ──────────────────────────────────────────
 * No hay nada aquí que sea de franquicias. Vive en la funcionalidad
 * porque hoy la usa una sola página; cuando haya una segunda, sube — y
 * este archivo desaparece de aquí en vez de duplicarse.
 */
export function ScrollGlobe({ sections, globe, motionOk, className }: ScrollGlobeProps) {
  const {
    activeSection,
    progress,
    globeTransform,
    globeHidden,
    containerRef,
    registerSection,
    scrollToSection,
  } = useScrollGlobe(sections.length, motionOk);

  return (
    <div
      ref={containerRef}
      className={cn("relative w-full max-w-full overflow-x-hidden", className)}
    >
      <ScrollProgressBar progress={progress} motionOk={motionOk} />

      <ScrollGlobeNav
        sections={sections}
        activeSection={activeSection}
        onSelect={scrollToSection}
        motionOk={motionOk}
      />

      {/*
        El globo. `pointer-events-none` para que no se coma los clics de
        lo que tiene debajo: es decoración a pantalla completa y sin esto
        se traga el botón de cualquier sección que le quede detrás.
      */}
      <div
        aria-hidden="true"
        className={cn(
          "pointer-events-none fixed z-10 will-change-transform",
          motionOk && "transition-all duration-[1400ms] ease-[cubic-bezier(0.23,1,0.32,1)]",
        )}
        style={{
          transform: globeTransform,
          opacity: globeHidden ? 0 : 0.9,
          visibility: globeHidden ? "hidden" : "visible",
        }}
      >
        <div className="scale-75 sm:scale-90 lg:scale-100">{globe}</div>
      </div>

      {sections.map((section, index) => (
        <ScrollGlobeSectionBlock
          key={section.id}
          ref={registerSection(index)}
          section={section}
          isFirst={index === 0}
        />
      ))}
    </div>
  );
}
